import React, { useReducer, useContext, useEffect, useCallback } from "react";
import { notification } from "antd";
import { IFamilyTreePerson } from "../../interfaces";
import AddMember from "../../views/modals/AddMember/AddMember";
import axios, { CancelTokenSource, AxiosResponse, AxiosError } from "axios";
import { IAuthContext, authContext } from "../../providers/AuthProvider";
import { Store } from "antd/lib/form/interface";
import { Backend } from "../../helpers";

export declare type EditMemberButtonProps = {
  person: IFamilyTreePerson;
  icon: React.ReactNode;
  onSuccess: (person: IFamilyTreePerson) => void;
};

export type AddMemberButtonState = {
  modalVisible: boolean;
  editPersonTS: CancelTokenSource;
  appContext: IAuthContext;
};

const EditMemberButton: React.FC<
  React.HTMLAttributes<HTMLDivElement> & EditMemberButtonProps
> = (props) => {
  const {
    person,
    onSuccess,
    onClick,
    onTouchEnd,
    children,
    className,
    icon,
    ...rest
  } = props;

  /**
   * States management
   */
  const reducer = (
    state: AddMemberButtonState,
    action: {
      type: "updateState" | "openModal" | "closeModal";
      state?: Partial<AddMemberButtonState>;
    }
  ): AddMemberButtonState => {
    switch (action.type) {
      case "updateState":
        return {
          modalVisible: action.state?.modalVisible ?? state.modalVisible,
          editPersonTS: action.state?.editPersonTS ?? state.editPersonTS,
          appContext: action.state?.appContext ?? state.appContext,
        };

      case "openModal": {
        const { modalVisible, ...rest } = state;
        return {
          ...rest,
          modalVisible: true,
        };
      }

      case "closeModal": {
        const { modalVisible, ...rest } = state;
        return {
          ...rest,
          modalVisible: false,
        };
      }

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    modalVisible: false,
    editPersonTS: axios.CancelToken.source(),
    appContext: useContext(authContext),
  });

  /**
   * Create a person
   */
  const editPerson = useCallback(
    async (values: Store) => {
      // start loading
      state.appContext.dispatch({ type: "startLoading" });
      // submit data
      await Backend.getInstance()
        .axios()
        .post(
          "/api/editnode/",
          {
            personId: person.id,
            personData: {
              first_name: values.first_name,
              middle_name: values.middle_name,
              last_name: values.last_name,
              dob: values.dob.format("YYYY-MM-DD"),
              gender: values.gender,
              birthplace: values.birthplace,
            },
          },
          {
            cancelToken: state.editPersonTS.token,
          }
        )
        .then((response: AxiosResponse<IFamilyTreePerson>) => {
          // notify user
          notification.success({
            message: `${[
              response.data.first_name,
              response.data.middle_name,
              response.data.last_name,
            ].join(" ")} has been successfully added in your tree!`,
          });
          // notify the parent component
          onSuccess(response.data);
        })
        .catch((error: AxiosError<any>) => {
          if (!axios.isCancel(error)) {
            notification.error({
              message: `Failed to edit ${[
                person.first_name,
                person.middle_name,
                person.last_name,
              ].join(" ")} details. Try again later please.`,
            });
          }
        })
        .finally(() => {
          // stop loading
          state.appContext.dispatch({ type: "stopLoading" });
        });
    },
    [state.appContext, state.editPersonTS, onSuccess, person]
  );

  useEffect(() => {
    return () => {
      state.editPersonTS.cancel();
    };
  }, [state.editPersonTS]);

  return (
    <>
      <div
        onClick={(e) => {
          // process
          dispatch({ type: "openModal" });
          if (onClick) onClick(e);
        }}
        onTouchEnd={(e) => {
          // process
          dispatch({ type: "openModal" });
          if (onTouchEnd) onTouchEnd(e);
        }}
        className={`rounded-full ${className ? " " + className : ""}`}
        {...rest}
      >
        {props.icon}
        {children}
      </div>
      <AddMember
        title="Edit Family Member"
        visible={state.modalVisible}
        initialValue={person}
        handleSubmit={async (values: Store) => {
          dispatch({ type: "closeModal" });
          await editPerson(values);
        }}
        onCancel={() => {
          dispatch({ type: "closeModal" });
        }}
      />
    </>
  );
};

export default EditMemberButton;
